/**
 * Normalizes payment plan breakdown data for UI display.
 *
 * The backend may return clearPatientFeeAmountInCents values per provider
 * that don't perfectly sum to match the installment's amountInCents.
 * This happens because the rounded clearPatientFee is distributed across
 * providers with small rounding errors.
 *
 * This function adjusts the clearPatientFeeAmountInCents of the provider
 * with the largest fee in each installment so that the sum of all providers'
 * clearDiscountedPrice matches the installment's amountInCents exactly.
 *
 * Only applies to installments that have clearPatientFee values.
 * Only adjusts clearPatientFeeAmountInCents (and recalculates clearDiscountedPrice).
 * Does NOT touch amountInCents (provider actual payment) or clearFeeAmountInCents.
 */
export function normalizePaymentPlanBreakdown(payments: any[]): any[] {
  if (!payments || !payments.length) {
    return payments;
  }

  // Check once if this breakdown has any clearPatientFee across all installments
  const hasClearPatientFee = payments.some((payment) =>
    payment.providerBreakdown?.some(
      (provider: any) =>
        provider.clearPatientFeeAmountInCents &&
        provider.clearPatientFeeAmountInCents > 0
    )
  );

  if (!hasClearPatientFee) {
    return payments;
  }

  return payments.map((payment) => {
    const { providerBreakdown, amountInCents: installmentAmount } = payment;

    if (!providerBreakdown || !providerBreakdown.length) {
      return payment;
    }

    // Calculate the current sum of clearDiscountedPrice across all providers
    // clearDiscountedPrice = amountInCents (provider) + clearFeeAmountInCents + clearPatientFeeAmountInCents
    const totalClearDiscountedPrice = providerBreakdown.reduce(
      (sum: number, provider: any) =>
        sum + (provider.clearDiscountedPrice || 0),
      0
    );

    // Calculate the difference between the sum and the installment amount
    const difference = totalClearDiscountedPrice - installmentAmount;

    // If there's no difference, no adjustment needed
    if (difference === 0) {
      return payment;
    }

    // Find the provider with the largest clearPatientFeeAmountInCents
    let largestFeeIndex = -1;
    let largestFee = 0;

    providerBreakdown.forEach((provider: any, index: number) => {
      const fee = provider.clearPatientFeeAmountInCents || 0;
      if (fee > largestFee) {
        largestFee = fee;
        largestFeeIndex = index;
      }
    });

    // If no provider has a clearPatientFee, skip adjustment
    if (largestFeeIndex === -1 || largestFee === 0) {
      return payment;
    }

    // Create a new providerBreakdown with the adjusted clearPatientFee
    const adjustedBreakdown = providerBreakdown.map(
      (provider: any, index: number) => {
        if (index !== largestFeeIndex) {
          return provider;
        }

        // Adjust the clearPatientFee by subtracting the difference
        // Ensure the fee never goes below 0
        const adjustedClearPatientFee = Math.max(
          0,
          (provider.clearPatientFeeAmountInCents || 0) - difference
        );

        // Recalculate clearDiscountedPrice with adjusted fee
        const adjustedClearDiscountedPrice =
          (provider.amountInCents || 0) +
          (provider.clearFeeAmountInCents || 0) +
          adjustedClearPatientFee;

        return {
          ...provider,
          clearPatientFeeAmountInCents: adjustedClearPatientFee,
          clearDiscountedPrice: adjustedClearDiscountedPrice,
        };
      }
    );

    return {
      ...payment,
      providerBreakdown: adjustedBreakdown,
    };
  });
}
